import React, { useState, useMemo, useRef } from 'react';
import {
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	Typography,
	Box,
	Checkbox,
	useTheme,
	Tooltip,
	IconButton,
} from '@mui/material';
import ArrowIcon from '../../assets/icons/ArrowIcon';
import styles from './StyledTable.module.scss';
import { useResolutionScale, useAutoFocusContextSafe } from '../../hooks';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

// ---- Sorting Utilities ----
const descendingComparator = (a, b, orderBy) => {
	if (b[orderBy] < a[orderBy]) return -1;
	if (b[orderBy] > a[orderBy]) return 1;
	return 0;
};

const getComparator = (order, orderBy) =>
	order === 'desc'
		? (a, b) => descendingComparator(a, b, orderBy)
		: (a, b) => -descendingComparator(a, b, orderBy);

const stableSort = (array, comparator) => {
	const stabilized = array.map((el, index) => [el, index]);
	stabilized.sort((a, b) => {
		const order = comparator(a[0], b[0]);
		if (order !== 0) return order;
		return a[1] - b[1];
	});
	return stabilized.map((el) => el[0]);
};

// ---- Main Table Component ----
const StyledTable = ({
	title = '',
	columns: originalColumns = [],
	rows: originalRows = [],
	filters = {},
	showCheckBox = false,
	elevated = false,
	selectedRows,
	onSelectionChange,
	sx = {},
	rowStyle = null,
	cellStyle = null,
	showRowCount = false,
	count = null,
	onHandleRowClick,
	selectall,
	maxHeight = '45vh',
	isRowClickCopy = false,
	fillHeight = false,
}) => {
	const theme = useTheme();
	const { scaleValue, getResponsiveValue, is4K } = useResolutionScale();
	const isDark = theme.palette.mode === 'dark';
	const autoFocusContext = useAutoFocusContextSafe();

	const [internalSelected, setInternalSelected] = useState([]);
	const [order, setOrder] = useState('asc');
	const [orderBy, setOrderBy] = useState(null);

	const [copiedCell, setCopiedCell] = useState(null);
	const copyTimeoutRef = useRef(null);

	const customColors = (sx && sx.tableColors) || {};

	const defaultColors = {
		text: isDark ? '#F8F8F8' : '#202020',
		// headerText: isDark ? '#F8F8F8' : '#202020',
		headerText: '#F8F8F8',
		border: isDark ? '#595959' : '#B0B0B0',
		rowHover: isDark ? 'rgba(255,255,255,0.04)' : '#F3F3F3',
		background: elevated ? (isDark ? '#151A1E' : '#F8F8F8') : 'transparent',
		// headerBackground: 'transparent',
		headerBackground: isDark ? '#0f172a' : '#0F107B',
	};

	const colors = { ...defaultColors, ...customColors };

	const safeArray = (value) => (Array.isArray(value) ? value : []);
	const safeOriginalColumns = safeArray(originalColumns);
	const safeOriginalRows = safeArray(originalRows);

	const rows = useMemo(() => {
		let visible = safeOriginalRows.filter((row) => !row.hidden);

		// Apply filters (if provided)
		if (filters && Object.keys(filters).length > 0) {
			visible = visible.filter((row) =>
				Object.entries(filters).every(([field, selectedValues]) => {
					if (!selectedValues?.length) return true;
					return selectedValues.includes(row[field]);
				}),
			);
		}

		return visible;
	}, [safeOriginalRows, filters]);

	const columns = useMemo(() => {
		const visibleColumns = safeOriginalColumns.filter((col) => !col.hidden);
		const hasLotNumber = rows.some((row) => {
			const lotNumber = row?.lotNumber;
			return lotNumber !== null && lotNumber !== undefined && String(lotNumber).trim() !== '';
		});

		return visibleColumns.filter(
			(col) => col.field !== 'lotNumber' || hasLotNumber,
		);
	}, [safeOriginalColumns, rows]);

	const selected = Array.isArray(selectedRows)
		? selectedRows
		: internalSelected;

	const updateSelection = (newSelection, row) => {
		if (onSelectionChange) onSelectionChange(newSelection, row);
		if (!selectedRows) setInternalSelected(newSelection);
	};

	const handleSelectAll = (checked) => {
		const allIds = checked ? rows.map((row) => row.id) : [];
		updateSelection(allIds);
	};

	const handleRowSelect = (id, row) => {
		if (typeof onHandleRowClick === 'function') {
			onHandleRowClick(id, row);
		}
		const shouldShowCheckbox =
			typeof row.showCheckBox === 'boolean' ? row.showCheckBox : showCheckBox;
		if (!shouldShowCheckbox) return;

		const isAlreadySelected = selected.includes(id);
		// Support multi-select: add/remove the clicked row from existing selection
		const newSelection = isAlreadySelected
			? selected.filter((selectedId) => selectedId !== id)
			: [...selected, id];
		updateSelection(newSelection, row);

		if (autoFocusContext?.focusNearestInput) {
			setTimeout(() => autoFocusContext.focusNearestInput(), 100);
		}
	};

	let hasTextSelection = false;

	const handleMouseDown = () => {
		hasTextSelection = false;
	};

	const handleMouseUp = () => {
		const selection = window.getSelection();
		hasTextSelection = selection && selection.toString().length > 0;
	};

	const handleRowSelectSafe = (id, row) => {
		if (hasTextSelection) return; // <-- block row click if user was selecting
		handleRowSelect(id, row);
	};

	const handleSort = (col) => {
		if (!col.sortable) return;
		const isAsc = orderBy === col.field && order === 'asc';
		setOrder(isAsc ? 'desc' : 'asc');
		setOrderBy(col.field);
	};

	const renderSortIcon = (col) => {
		if (!col.sortable) return null;
		const isActive = orderBy === col.field;
		if (!isActive) {
			return (
				<Box
					sx={{ display: 'inline-flex', alignItems: 'center', height: '16px' }}
				>
					<ArrowIcon direction="down" size={scaleValue(18)} />
				</Box>
			);
		}
		return (
			<ArrowIcon
				direction={order === 'asc' ? 'up' : 'down'}
				size={scaleValue(18)}
			/>
		);
	};

	const sortedRows = useMemo(() => {
		if (!orderBy) return rows;
		return stableSort(rows, getComparator(order, orderBy));
	}, [rows, order, orderBy]);

	const anyRowHasCheckbox =
		columns?.length > 0 &&
		(showCheckBox || sortedRows?.some((row) => row?.showCheckBox === true));

	const cols =
		Array.isArray(columns) && columns.length > 0
			? columns.map((c) => {
					const flex = typeof c.flex === 'number' ? c.flex : 1;
					const computedWidth = c.width
						? typeof c.width === 'number'
							? `${c.width}px`
							: c.width
						: `${flex}fr`;

					return { ...c, computedWidth };
			  })
			: [];

	return (
		<Box
			sx={(theme) => ({
				width: '100%',
				display: 'flex',
				flexDirection: 'column',
				height: fillHeight ? '100%' : 'auto',
				minHeight: fillHeight ? 0 : undefined,
				...(typeof sx === 'function' ? sx(theme) : sx),
			})}
		>
			{title && (
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						padding: '4px 16px',
						gap: 2,
						flexWrap: { xs: 'wrap', md: 'nowrap' },
					}}
				>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						{showRowCount && (
							<Typography
								sx={{
									fontFamily: "'Uni Neue','jlr-emeric', Arial, sans-serif",
									fontWeight: 400,
									fontSize: { md: '24px', lg: '44px' },
									color: colors.text,
								}}
							>
								{count ?? rows.length}
							</Typography>
						)}
						<Typography
							variant='inherit'
							sx={{
								fontWeight: 600,
								fontSize: `${scaleValue(24)}px`,
								color: colors.text,
							}}
						>
							{`${title} (${rows.length})`}
						</Typography>
					</Box>
				</Box>
			)}

			{/* Table content remains identical */}
			<Box
				sx={{
					flex: 1,
					display: 'flex',
					flexDirection: 'column',
					minHeight: fillHeight ? 0 : undefined,
					borderRadius: elevated ? '24px' : 0,
					boxShadow: elevated ? '0px 0px 4px 0px #00000040' : 'none',
					backgroundColor: colors.background,
					overflow: 'hidden',
					paddingBottom: '6px',
				}}
			>
				<Box
					sx={{
						position: 'relative',
						overflowX: 'auto',
						overflowY: 'auto',
						maxHeight,
						width: '100%',
						...(fillHeight && {
            	flex: 1,
            	minHeight: 0,
        		}),
					}}
				>
					{columns.length === 0 ? (
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								height: '30vh',
								backgroundColor: colors.background,
								color: colors.text,
								fontSize: scaleValue(16),
								fontWeight: 500,
							}}
						>
							No columns selected
						</Box>
					) : (
						<Table
							sx={{
								width: '100%',
								tableLayout: 'fixed',
								backgroundColor: colors.background,
								borderRadius: '10px 10px 0 0',
							}}
						>
							<TableHead
								sx={{
									position: 'sticky',
									top: 0,
									zIndex: 2,
									backgroundColor: colors.headerBackground,
								}}
							>
								<TableRow
									sx={{
										height: scaleValue(58),
										borderBottom: `1px solid ${colors.border}`,
										backgroundColor: colors.headerBackground,
									}}
								>
									{anyRowHasCheckbox && (
										<TableCell
											padding="checkbox"
											sx={{
												width: scaleValue(50),
												textAlign: 'center',
												justifyContent: 'center',
												alignItems: 'center',
												backgroundColor: colors.headerBackground,
											}}
										>
											{selectall && showCheckBox && (
												<Checkbox
													disableRipple
													checked={
														rows.length > 0 && selected.length === rows.length
													}
													indeterminate={
														selected.length > 0 && selected.length < rows.length
													}
													onChange={(e) => handleSelectAll(e.target.checked)}
													sx={{
														transform: is4K ? 'scale(1.8)' : 'scale(1)',
													}}
												/>
											)}
										</TableCell>
									)}
									{cols.map((col) => (
										<TableCell
											key={col.field}
											onClick={() => handleSort(col)}
											sx={{
												fontFamily: "'Uni Neue', sans-serif",
												fontWeight: 800,
												fontSize: scaleValue(16),
												color: colors.headerText,
												borderBottom: `1px solid ${colors.border}`,
												width: scaleValue(col.computedWidth),
												minWidth: scaleValue(col.minWidth ?? 80),
												cursor: col.sortable ? 'pointer' : 'default',
												userSelect: 'none',
												textAlign: col.align || 'center',
											}}
										>
											<Tooltip title={col.title}>
												<Box
													sx={{
														display: 'flex',
														alignItems: 'center',
														gap: 1,
														justifyContent:
															col.align === 'right'
																? 'flex-end'
																: col.align === 'left'
																? 'flex-start'
																: 'center',
													}}
												>
													{col.title}
													{renderSortIcon(col)}
												</Box>
											</Tooltip>
										</TableCell>
									))}
								</TableRow>
							</TableHead>

							<TableBody>
								{sortedRows.length > 0 ? (
									sortedRows.map((row, index) => {
										const shouldShowCheckbox =
											(typeof row.showCheckBox === 'boolean'
												? row.showCheckBox
												: showCheckBox) && columns?.length > 0;
										const isSelected =showCheckBox && selected.includes(row.id);
										// {console.log(isSelected)}
								return (
											<TableRow
												key={row.id}
												hover
												onMouseDown={handleMouseDown}
												onMouseUp={handleMouseUp}
												onClick={() => handleRowSelectSafe(row.id, row)}
												sx={{
													backgroundColor: isSelected
														? isDark
															? '#38384dff'
															: '#d8d3e5ff'
														: index % 2 === 1
														? isDark
															? '#1E1E2D'
															: '#E8E5F0'
														: colors.background,
													'&:hover': {
														backgroundColor: isDark ? '#2A2A2A' : '#F3F3F3',
													},
													cursor: shouldShowCheckbox ? 'pointer' : 'default',
													height: 36,
													...(typeof rowStyle === 'function'
														? rowStyle(row, theme.palette.mode)
														: {}),
												}}
											>
												{anyRowHasCheckbox && (
													<TableCell
														padding="checkbox"
														sx={{
															width: scaleValue(50),
															textAlign: 'center',
															// visibility: shouldShowCheckbox
															// 	? 'visible'
															// 	: 'hidden',
														}}
													>
						           	{shouldShowCheckbox && (
															<Checkbox
																 checked={isSelected}
																onChange={() => handleRowSelect(row.id, row)}
																sx={{
																	transform: is4K ? 'scale(1.8)' : 'scale(1)',
																}}
															/>
														)}
													</TableCell>
												)}
												{cols.map((col) => {
													const value = row[col.field] ?? '-';
													return (
														<TableCell
															key={col.field}
															onClick={(e) => {
																if (!isRowClickCopy || hasTextSelection) return;

																e.stopPropagation();

																navigator.clipboard.writeText(String(value));

																setCopiedCell(`${row.id}-${col.field}`);

																if (copyTimeoutRef.current) {
																	clearTimeout(copyTimeoutRef.current);
																}

																copyTimeoutRef.current = setTimeout(() => {
																	setCopiedCell(null);
																}, 1000);
															}}
															sx={{
																cursor: isRowClickCopy ? 'pointer' : 'default',
																py: 2,
																fontSize: scaleValue(14),
																fontFamily: "'Uni Neue', sans-serif",
																fontWeight: 800,
																color: colors.text,
																width: scaleValue(col.computedWidth),
																minWidth: scaleValue(col.minWidth ?? 80),
																whiteSpace: 'nowrap',
																overflow: 'hidden',
																textOverflow: 'ellipsis',
																padding: `${scaleValue(8)}px`,
																textAlign: col.align || 'center',
																...(typeof col.cellStyle === 'function'
																	? col.cellStyle(row, col, theme.palette.mode)
																	: col.cellStyle || {}),
																...(typeof cellStyle === 'function'
																	? cellStyle(row, col, theme.palette.mode)
																	: {}),
															}}
														>
															{(() => {
																const cellKey = `${row.id}-${col.field}`;
																const isCopied = copiedCell === cellKey;

																const tooltipTitle = isRowClickCopy ? (
																	isCopied ? (
																		'✅ Copied'
																	) : (
																		'👆 Click to copy'
																	)
																) : (
																	<Box
																		sx={{
																			display: 'flex',
																			alignItems: 'center',
																			gap: 1,
																		}}
																	>
																		<Typography sx={{ fontSize: 'inherit' }}>
																			{String(value)}
																		</Typography>
																		<IconButton
																			size="small"
																			onClick={(e) => {
																				e.stopPropagation();
																				navigator.clipboard.writeText(
																					String(value),
																				);
																			}}
																			sx={{
																				padding: 0,
																				width: scaleValue(24),
																				height: scaleValue(24),
																				color: 'inherit',
																			}}
																		>
																			<ContentCopyIcon
																				sx={{ fontSize: scaleValue(16) }}
																			/>
																		</IconButton>
																	</Box>
																);

																return (
																	<Tooltip title={tooltipTitle} enterTouchDelay={300} arrow>
																		{col.render ? (
																			<Box
																				component="span"
																				sx={{
																					display: 'inline-flex',
																					width: '100%',
																					justifyContent:
																						col.align === 'right'
																							? 'flex-end'
																							: col.align === 'center'
																							? 'center'
																							: 'flex-start',
																				}}
																			>
																				{col.render(row, theme)}
																			</Box>
																		) : (
																			<Typography
																				sx={{
																					fontSize: 'inherit',
																					fontWeight: 'inherit',
																					color: 'inherit',
																					whiteSpace: 'nowrap',
																					overflow: 'hidden',
																					textOverflow: 'ellipsis',
																					userSelect: 'text',
																				}}
																			>
																				{String(value)}
																			</Typography>
																		)}
																	</Tooltip>
																);
															})()}
														</TableCell>
													);
												})}
											</TableRow>
										);
									})
								) : (
									<TableRow>
										<TableCell
											colSpan={columns.length + (showCheckBox ? 1 : 0)}
											sx={{
												textAlign: 'center',
												fontSize: getResponsiveValue({
													laptop: 16,
													fhd: 18,
													qhd: 20,
												}),
												fontWeight: 400,
												color: colors.text,
												padding: `${scaleValue(16)}px`,
											}}
										>
											No records found
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					)}
				</Box>
			</Box>
		</Box>
	);
};

StyledTable.defaultProps = {
	columns: [],
	rows: [],
	filters: {},
	showCheckBox: false,
	elevated: false,
	selectedRows: undefined,
	onSelectionChange: undefined,
	sx: {},
	rowStyle: null,
	cellStyle: null,
	showRowCount: false,
	count: null,
	onHandleRowClick: undefined,
	selectall: false,
	maxHeight: '45vh',
};

export default StyledTable;
